'use client';

import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { useAuth, useSignIn } from '@clerk/nextjs';
import useActivateSession from '../hooks/useActivateSession';

export default function SessionActivator() {
  const router = useRouter();
  const { isSignedIn } = useAuth();
  const { signIn, isLoaded } = useSignIn();
  const activateSession = useActivateSession();
  const [tried, setTried] = useState(false);
  useEffect(() => {
    // We only want to run this once per page load
    if (!isLoaded || isSignedIn || !signIn || !router.isReady || tried) return;

    const { signin_token, redirect_to } = router.query as {
      signin_token?: string;
      redirect_to?: string;
    };

    // If there's no signin_token, do nothing.
    if (!signin_token) {
      setTried(true);
      return;
    }

    (async () => {
      try {
        const response = await signIn.create({ strategy: 'ticket', ticket: signin_token });
        await activateSession({
          session: response.createdSessionId,
          // If the URL also passed a “redirect_to”, forward it; otherwise fallback to "/"
          redirectTo: typeof redirect_to === 'string' ? redirect_to : '/',
        });
      } catch (err) {
        console.error('[SessionActivator] failed to set active session:', err);
      } finally {
        setTried(true);

        // Clean up the URL by removing ?session_token and ?redirect_to
        // (so that future client‐side navigation doesn’t re‐trigger)
        const { pathname, query } = router;
        delete (query as any).session_token;
        delete (query as any).redirect_to;
        router.replace({ pathname, query }, undefined, { shallow: true });
      }
    })();
  }, [router, activateSession, signIn, isLoaded, isSignedIn, tried]);

  return null;
}
